/**
 * API Client — wraps fetch with Entra ID bearer tokens
 *
 * All requests go through the .NET Backend which validates the token
 * and proxies orchestration calls to Flight Director.
 */

import { getAccessToken } from './auth';

// Use ?? (not ||) so an explicit empty string from runtime env-config.js wins over
// the localhost fallback — empty BASE_URL means "use same origin" (nginx-proxied).
const BASE_URL = window._env_?.VITE_API_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

/**
 * Authenticated fetch wrapper.
 * Automatically attaches the Entra ID access token as a Bearer header.
 */
export async function apiFetch(path, options = {}) {
  const token = await getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token invalid — force re-login
    const { login } = await import(/* @vite-ignore */ './auth');
    await login();
    return; // page will redirect
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`API ${response.status}: ${body}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }
  return response.text();
}

// ------------------------------------------------------------------
// Convenience methods
// ------------------------------------------------------------------

/** GET /api/auth/me — current user profile from Entra ID claims */
export function getMe() {
  return apiFetch('/api/auth/me');
}

/** GET /api/ws/negotiate — get WebSocket connection info */
export function negotiateWebSocket() {
  return apiFetch('/api/ws/negotiate');
}

// ------------------------------------------------------------------
// Flight Director proxy helpers (Backend strips /api/fd/ prefix)
// ------------------------------------------------------------------

/** POST a query to the orchestrator */
export function submitQuery(goal, options = {}) {
  return apiFetch('/api/fd/api/orchestration/query', {
    method: 'POST',
    body: JSON.stringify({ goal, ...options }),
  });
}

/** GET registered agents */
export function getAgents() {
  return apiFetch('/api/fd/api/agents');
}

/** GET orchestration sessions */
export function getSessions() {
  return apiFetch('/api/fd/api/orchestration/sessions');
}

// ------------------------------------------------------------------
// Trigger helpers
// ------------------------------------------------------------------

/** GET all triggers */
export function getTriggers() {
  return apiFetch('/api/fd/api/triggers');
}

/** POST run a trigger immediately */
export function runTriggerNow(triggerId, { triggeredByEmail } = {}) {
  return apiFetch(`/api/fd/api/triggers/${triggerId}/run-now`, {
    method: 'POST',
    body: JSON.stringify({
      triggered_by_email: triggeredByEmail || null,
    }),
  });
}
